import classnames from 'classnames';
import {
  Conversation as ConversationT,
  MetaConversation,
  Tag,
} from 'lib-shared-legacy-types';
import type {
  Conversation as ConversationType,
  User,
} from 'lib-shared-types-api';
import _isEqual from 'lodash/isEqual';
import { Component } from 'react';

import {
  ConversationContextProvider,
  ConversationStatusColorBox,
} from 'components/conversation';
import { RouteComponentProps } from 'hocs/router';

import {
  getConversationTitle,
  getHumanConversationChannel,
} from '../../../app/utils/conversationTitle';
import { ActivityLog } from '../../activityLog/components/activityLog';
import ConversationSidebar from '../containers/conversationSidebar';
import MessageSection from '../containers/messageSection';
import ReplySection from '../containers/replySection';
import Timeline from '../containers/timeline';
import { canClaimConversation } from '../utils/canClaimConversation';
import { canTakeOverConversation } from '../utils/canTakeOverConversation';

import { ConversationSummary } from './conversationSummary';
import { ConversationHeader } from './header';
import { SubConversationList } from './subConversationList';
import { SubConversationListItem } from './subConversationList/subConversationList';

import '../styles/conversation.less';

type OwnProps = {
  metaConversation: MetaConversation;
  latestMessengerConversation: any;
  conversationClaim: any;
  telephonyMakeCall: any;
  composeEmailOpenModal: any;
  asyncStatus: any;
  isMenuOpen: boolean;
  isCustomCardMenuOpen: boolean;
  conversationUnmount: any;
  conversationResetUnread: (payload: any) => void;
  conversationGet: (payload: any) => Promise<any>;
  notificationTabBadgeReset: () => void;
  isActivityLogOpen: boolean;
  isFocusMode: boolean;
  closeSideBarMenu: () => void;
};

type PropsT = OwnProps & RouteComponentProps<'csid'>;

class Conversation extends Component<PropsT> {
  constructor(props: PropsT) {
    super(props);
    this.getCsid = this.getCsid.bind(this);
  }

  componentDidMount() {
    const csid = this.getCsid(this.props.params);
    if (csid) {
      this.prepConversationByCSID(csid);
    }
  }

  shouldComponentUpdate(nProps: PropsT) {
    return !_isEqual(this.props, nProps);
  }

  componentDidUpdate(prevProps: PropsT) {
    const csid = this.getCsid(this.props.params);
    if (csid && csid !== this.getCsid(prevProps.params)) {
      this.prepConversationByCSID(csid);
    }
  }

  componentWillUnmount() {
    this.props.conversationUnmount();
  }

  prepConversationByCSID(csid: number) {
    if (csid) {
      this.props.conversationResetUnread({ csid });
      this.props.conversationGet({ csid }).catch((err: Error) => {
        console.error('Caught', err);
        return err;
      });
      this.props.notificationTabBadgeReset();
    }
  }

  getCsid(params) {
    const { csid } = params;
    if (csid !== undefined && typeof csid === 'string') {
      return parseInt(csid, 10);
    }
    return undefined;
  }

  getConversationAgent(conversation) {
    return conversation &&
      conversation.assignment &&
      conversation.assignment.agent &&
      typeof conversation.assignment.agent.name === 'string'
      ? conversation.assignment.agent.name
      : null;
  }

  getConversationRequesterDetails(requester) {
    return requester
      ? requester.name || requester.email || requester.phone_number || null
      : null;
  }

  getConversationQueue(conversation) {
    return conversation &&
      conversation.queue &&
      typeof conversation.queue === 'string'
      ? conversation.queue
      : null;
  }

  canFollowUpConversation(): boolean {
    const { metaConversation } = this.props;
    const isAssigned =
      metaConversation.conversation.assignment === null ? false : true;

    if (isAssigned) {
      return true;
    }
    return false;
  }

  renderContent(params: {
    showTakeoverButton: boolean;
    showClaimButton: boolean;
  }) {
    const { isFocusMode } = this.props;
    const { showTakeoverButton, showClaimButton } = params;
    const { requester } = this.props.metaConversation;

    return this.props.isActivityLogOpen ? (
      <ActivityLog />
    ) : (
      <div
        className={classnames('content message-content scrollable', {
          'focus-mode': isFocusMode,
        })}
        style={{ padding: 0 }}
      >
        <MessageSection />

        {!requester?.anonymized_at && (
          <ReplySection
            showTakeoverButton={showTakeoverButton}
            showClaimButton={showTakeoverButton ? false : showClaimButton}
          />
        )}
      </div>
    );
  }

  render() {
    const {
      metaConversation,
      navigate,
      latestMessengerConversation,
      isCustomCardMenuOpen,
      isFocusMode,
    } = this.props;
    const {
      parentConversation,
      parentConversationRequester,
      linkedConversations,
      conversation,
      isAssignedToMe,
    } = metaConversation;

    const showTakeoverButton = canTakeOverConversation(
      conversation,
      isAssignedToMe,
    );
    const showClaimButton = canClaimConversation(
      conversation,
      isAssignedToMe,
      latestMessengerConversation,
    );

    return (
      <ConversationContextProvider conversation={metaConversation.conversation}>
        <div className="conversation-view">
          {!parentConversation && (
            <div
              className={classnames('conversation-view__timeline', {
                'conversation-view__timeline--placeholder':
                  !!parentConversation,
                'conversation-view__focus-mode': isFocusMode,
              })}
            >
              <Timeline />
            </div>
          )}
          <div className="conversation-view__main">
            {!!parentConversation && (
              <div
                className="conversation-view__parent"
                onClick={() =>
                  navigate('/conversation/' + parentConversation.id)
                }
                data-status={parentConversation.status}
              >
                <ConversationStatusColorBox
                  className="conversation-status"
                  csid={parentConversation.id as ConversationType['id']}
                  status={
                    parentConversation.status as ConversationType['status']
                  }
                />

                <ConversationSummary
                  className="conversation-view__parent__summary"
                  conversationAttributes={
                    metaConversation.conversation.attributes
                  }
                  conversationInfo={parentConversation}
                  labels={{
                    title: getConversationTitle({
                      conversation: parentConversation,
                      requester: parentConversationRequester,
                    }),
                    agent: this.getConversationAgent(parentConversation),
                    requester: this.getConversationRequesterDetails(
                      parentConversationRequester,
                    ),
                    queue: this.getConversationQueue(parentConversation),
                    channel: getHumanConversationChannel(
                      parentConversation,
                    ) as any,
                  }}
                />
              </div>
            )}
            <div
              data-status={this.props.metaConversation.conversation.status}
              className="conversation-view__current conversation-messages"
            >
              <ConversationHeader />
              {this.renderContent({ showTakeoverButton, showClaimButton })}
            </div>
            {!parentConversation &&
              linkedConversations &&
              linkedConversations.length > 0 && (
                <div className="conversation-view__children">
                  <SubConversationList
                    conversations={linkedConversations.map<SubConversationListItem>(
                      (metaC: {
                        conversation: ConversationT;
                        notes: Array<any>;
                        requester: User;
                        tags: Array<Tag>;
                      }) => {
                        return {
                          info: metaC.conversation,
                          requester: metaC.requester,
                          labels: {
                            title: getConversationTitle(metaC),
                            agent: this.getConversationAgent(
                              metaC.conversation,
                            ),
                            queue: this.getConversationQueue(
                              metaC.conversation,
                            ),
                            channel: getHumanConversationChannel(
                              metaC.conversation,
                            ),
                          },
                        } as SubConversationListItem;
                      },
                    )}
                    onConversationClick={(csid: number | string) =>
                      navigate('/conversation/' + csid)
                    }
                  />
                </div>
              )}
          </div>
          <div
            className={classnames('conversation-view__sidebar', {
              closed: !isCustomCardMenuOpen,
            })}
          >
            <ConversationSidebar />
          </div>

          <div
            className={classnames('conversation-view__backdrop', {
              closed: !isCustomCardMenuOpen,
            })}
            onClick={this.props.closeSideBarMenu}
          />
        </div>
      </ConversationContextProvider>
    );
  }
}

export default Conversation;
